import React, { useRef, useEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import CharacterAssets from '@/utils/CharacterAssets';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

interface PlayerAvatarProps {
  username: string;
  avatar?: string;
  size?: AvatarSize;
  className?: string;
}

// Size configurations for different avatar sizes
const AVATAR_SIZES = {
  xs: { 
    canvas: 16, 
    tailwind: 'w-4 h-4',
    iconSize: 'text-[8px]'
  },
  sm: { 
    canvas: 32, 
    tailwind: 'w-8 h-8',
    iconSize: 'text-xs'
  },
  md: { 
    canvas: 48, 
    tailwind: 'w-12 h-12',
    iconSize: 'text-sm'
  },
  lg: { 
    canvas: 64, 
    tailwind: 'w-16 h-16',
    iconSize: 'text-base'
  }
} as const;

const PlayerAvatar: React.FC<PlayerAvatarProps> = ({ 
  username, 
  avatar,
  size = 'sm', 
  className = '' 
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const sizeConfig = AVATAR_SIZES[size];

  useEffect(() => {
    if (!avatar || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Check if we have a valid character ID
    if (!CharacterAssets.isValidCharacterId(avatar)) {
      setImageError(true);
      return;
    }

    const assetPath = CharacterAssets.getAssetPath(avatar);
    if (!assetPath) {
      setImageError(true);
      return;
    }

    const image = new Image();
    image.onload = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Get the idle preview frame (cropped to show character body)
      const frame = CharacterAssets.getIdlePreviewFrame(avatar);
      if (!frame) {
        setImageError(true);
        return;
      }

      // Disable antialiasing for pixel-perfect rendering
      ctx.imageSmoothingEnabled = false;

      
      
      // Use integer scaling for pixel art
      let scale: number;
      if (canvas.width <= 32) {
        scale = 1; // 1x for small sizes
      } else if (canvas.width <= 48) {
        scale = 2; // 2x for medium
      } else {
        scale = 3; // 3x for large
      }
      
      const width = frame.width * scale;
      const height = frame.height * scale;
      const x = Math.floor((canvas.width - width) / 2);
      const y = Math.floor((canvas.height - height) / 2);
      
      // Draw the character sprite with integer positioning
      ctx.drawImage(
        image,
        frame.x, frame.y, frame.width, frame.height, // Source
        x, y, width, height // Destination
      );

      setImageLoaded(true);
      setImageError(false);
    };

    image.onerror = () => {
      setImageError(true);
      setImageLoaded(false);
    };

    image.src = `/assets/${assetPath}`;
  }, [avatar, size]);

  // If we have an avatar and it's loaded successfully, show the canvas
  if (avatar && !imageError && CharacterAssets.isValidCharacterId(avatar)) {
    return (
      <div 
        className={`${sizeConfig.tailwind} relative rounded-full overflow-hidden bg-teal-500 ring-2 ring-white ${className}`}
        title={`${username}'s avatar`}
      >
        <canvas
          ref={canvasRef}
          width={sizeConfig.canvas}
          height={sizeConfig.canvas}
          className="w-full h-full"
        />
      </div>
    );
  }

  // Fallback to dark circle with icon
  return (
    <div 
      className={`${sizeConfig.tailwind} rounded-full flex items-center justify-center bg-teal-500 ring-2 ring-white ${className}`}
      title={`${username}'s avatar`}
    >
      <FaUser className={`text-white ${sizeConfig.iconSize}`} />
    </div>
  );
};

export { PlayerAvatar };
export default PlayerAvatar;